import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useProfiles } from "@/hooks/useProfiles";
import { Project } from "@/hooks/useProjects";

export type UserRole = 'ADMIN' | 'SUPERVISOR' | 'PADRAO' | 'VISUALIZACAO';

export const usePermissions = () => {
  const { user } = useAuth();
  const { data: profiles = [], isLoading } = useProfiles();
  const [isProfileReady, setIsProfileReady] = React.useState(false);

  const getCurrentUserProfile = () => {
    if (!user) return null;
    return profiles.find(p => p.id === user.id);
  };

  // Garantir que o perfil está carregado (simplificado sem timeout)
  React.useEffect(() => {
    // Se não há usuário ou ainda está carregando, não está pronto
    if (!user || isLoading) {
      setIsProfileReady(false);
      return;
    }
    
    // Se profiles foram carregados (mesmo que vazio ou sem encontrar o perfil específico),
    // considerar pronto para não bloquear a UI
    setIsProfileReady(true);
  }, [user, isLoading]);

  const getUserRole = (): UserRole => {
    const profile = getCurrentUserProfile();
    return (profile?.role as UserRole) || 'PADRAO';
  };

  const canEditProject = (project: Project) => {
    if (!user) return false;
    
    const currentProfile = getCurrentUserProfile();
    if (!currentProfile) return false;

    // Admin e Supervisor podem editar qualquer projeto
    if (['ADMIN', 'SUPERVISOR'].includes(currentProfile.role)) {
      return true;
    }

    // Coordenador SGTI pode editar
    if (project.sgti_coordinator_id === user.id) {
      return true;
    }

    // Usuários na lista de editores podem editar
    if (project.editor_ids && project.editor_ids.includes(user.id)) {
      return true;
    }

    return false;
  };

  const canViewProject = (project: Project) => {
    if (!user) return false;
    
    const currentProfile = getCurrentUserProfile();
    if (!currentProfile) return false;

    // Admin e Supervisor podem ver qualquer projeto
    if (['ADMIN', 'SUPERVISOR'].includes(currentProfile.role)) {
      return true;
    }

    // Usuários só veem projetos onde estão nomeados
    return (
      project.sgti_coordinator_id === user.id ||
      project.requisitante_id === user.id ||
      project.tecnico_id === user.id ||
      project.administrativo_id === user.id ||
      (project.editor_ids && project.editor_ids.includes(user.id)) ||
      (project.additional_members && project.additional_members.some((m: any) => m.memberId === user.id))
    );
  };

  const isAdmin = () => {
    const currentProfile = getCurrentUserProfile();
    return currentProfile?.role === 'ADMIN';
  };

  const isSupervisor = () => {
    const currentProfile = getCurrentUserProfile();
    return currentProfile?.role === 'SUPERVISOR';
  };

  const isViewOnly = () => {
    const currentProfile = getCurrentUserProfile();
    return currentProfile?.role === 'VISUALIZACAO';
  };

  const isPadrao = () => {
    const currentProfile = getCurrentUserProfile();
    return currentProfile?.role === 'PADRAO';
  };

  const canManageUsers = () => {
    const currentProfile = getCurrentUserProfile();
    return ['ADMIN', 'SUPERVISOR'].includes(currentProfile?.role || '');
  };

  // Novos métodos para controle de acesso a módulos
  const canAccessDashboard = () => {
    return ['ADMIN', 'SUPERVISOR'].includes(getUserRole());
  };

  const canAccessTasks = () => {
    return ['ADMIN', 'SUPERVISOR', 'PADRAO'].includes(getUserRole());
  };

  const canAccessReports = () => {
    return ['ADMIN', 'SUPERVISOR'].includes(getUserRole());
  };

  const canAccessPortfolios = () => {
    return ['ADMIN', 'SUPERVISOR'].includes(getUserRole());
  };

  const canAccessCalendar = () => {
    return ['ADMIN', 'SUPERVISOR', 'PADRAO'].includes(getUserRole());
  };

  const canAccessAdmin = () => {
    return ['ADMIN', 'SUPERVISOR'].includes(getUserRole());
  };

  const canAccessTrash = () => {
    return ['ADMIN', 'SUPERVISOR'].includes(getUserRole());
  };

  /**
   * Verifica se o usuário pode acessar o módulo de Gestão Estratégica.
   * 
   * Retorna true para:
   * - ADMIN ou SUPERVISOR (sempre)
   * - PADRAO que participa de planos estratégicos (verificado externamente via useStrategicParticipation)
   * 
   * NOTA: Este método apenas verifica o papel. A verificação de participação
   * é feita pelo hook useStrategicParticipation separadamente para evitar
   * dependência circular e manter a lógica de cache.
   * 
   * @param hasStrategicParticipation - Resultado do hook useStrategicParticipation
   */
  const canAccessStrategicModule = (hasStrategicParticipation: boolean = false) => {
    const role = getUserRole();
    if (['ADMIN', 'SUPERVISOR'].includes(role)) {
      return true;
    }
    return hasStrategicParticipation;
  };

  return {
    user,
    getCurrentUserProfile,
    getUserRole,
    canEditProject,
    canViewProject,
    isAdmin,
    isSupervisor,
    isViewOnly,
    isPadrao,
    canManageUsers,
    canAccessDashboard,
    canAccessTasks,
    canAccessReports,
    canAccessPortfolios,
    canAccessCalendar,
    canAccessAdmin,
    canAccessTrash,
    canAccessStrategicModule,
    isLoadingPermissions: isLoading || !isProfileReady
  };
};